import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  AlertTriangle, Check, RefreshCw, Search, Filter,
  Clock, User, Globe, Code, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Server, Monitor, TrendingUp,
  AlertCircle, Activity, Trash2, CheckCheck
} from 'lucide-react';
import { API } from '../config/api';

export default function AdminErrorMonitorPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('server');
  
  // Server errors state
  const [serverErrors, setServerErrors] = useState([]);
  const [serverStats, setServerStats] = useState(null);
  const [serverLoading, setServerLoading] = useState(true);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPage, setServerPage] = useState(0);
  const [serverFilter, setServerFilter] = useState({ endpoint: '', status_code: '', resolved: null });
  const [hoursFilter, setHoursFilter] = useState(24);
  
  // Client errors state
  const [clientErrors, setClientErrors] = useState([]);
  const [clientLoading, setClientLoading] = useState(true);
  const [clientTotal, setClientTotal] = useState(0);
  const [clientPage, setClientPage] = useState(0);
  const [clientFilter, setClientFilter] = useState({ route: '', resolved: null });
  
  const [expandedIds, setExpandedIds] = useState(new Set());
  const limit = 20;

  useEffect(() => {
    if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  // Fetch server errors and stats
  const fetchServerErrors = useCallback(async () => {
    setServerLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (serverPage * limit).toString(),
        hours: hoursFilter.toString()
      });
      if (serverFilter.endpoint) params.set('endpoint', serverFilter.endpoint);
      if (serverFilter.status_code) params.set('status_code', serverFilter.status_code);
      if (serverFilter.resolved !== null) params.set('resolved', serverFilter.resolved.toString());

      const [errorsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/server-errors?${params}`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/server-errors/stats?hours=${hoursFilter}`, { headers: getAuthHeaders() })
      ]);

      if (errorsRes.ok) {
        const data = await errorsRes.json();
        setServerErrors(data.errors || []);
        setServerTotal(data.total || 0);
      }
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setServerStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch server errors:', error);
    } finally {
      setServerLoading(false);
    }
  }, [serverPage, serverFilter, hoursFilter, getAuthHeaders]);

  // Fetch client errors
  const fetchClientErrors = useCallback(async () => {
    setClientLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (clientPage * limit).toString()
      });
      if (clientFilter.route) params.set('route', clientFilter.route);
      if (clientFilter.resolved !== null) params.set('resolved', clientFilter.resolved.toString());

      const response = await fetch(`${API}/admin/client-errors?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setClientErrors(data.errors || []);
        setClientTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch client errors:', error);
    } finally {
      setClientLoading(false);
    }
  }, [clientPage, clientFilter, getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'server') {
      fetchServerErrors();
    } else {
      fetchClientErrors();
    }
  }, [activeTab, fetchServerErrors, fetchClientErrors]);

  const handleResolveServer = async (errorId) => {
    try {
      const response = await fetch(`${API}/admin/server-errors/${errorId}/resolve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Error marked as resolved');
        fetchServerErrors();
      }
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const handleResolveClient = async (errorId) => {
    try {
      const response = await fetch(`${API}/admin/client-errors/${errorId}/resolve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Error marked as resolved');
        fetchClientErrors();
      }
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const handleBulkResolve = async () => {
    if (!confirm('Resolve all filtered errors?')) return;
    
    try {
      const response = await fetch(`${API}/admin/server-errors/resolve-all`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: serverFilter.endpoint || undefined,
          status_code: serverFilter.status_code ? parseInt(serverFilter.status_code) : undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Resolved ${data.resolved_count} errors`);
        fetchServerErrors();
      }
    } catch (error) {
      toast.error('Failed to bulk resolve');
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusColor = (code) => {
    if (code >= 500) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (code >= 400) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  const getErrorTypeColor = (type) => {
    switch (type) {
      case 'unhandled_exception': return 'bg-red-500/20 text-red-400';
      case 'http_5xx': return 'bg-orange-500/20 text-orange-400';
      case 'react_error_boundary': return 'bg-red-500/20 text-red-400';
      case 'window_onerror': return 'bg-orange-500/20 text-orange-400';
      case 'unhandled_rejection': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-background p-6" data-testid="admin-error-monitor">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              Error Monitor
            </h1>
            <p className="text-muted-foreground">Track and resolve server & client errors proactively</p>
          </div>
          <div className="flex gap-2">
            <select 
              value={hoursFilter}
              onChange={(e) => setHoursFilter(parseInt(e.target.value))}
              className="px-3 py-2 rounded-md bg-background border border-border text-sm"
            >
              <option value={1}>Last 1 hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </select>
            <Button 
              onClick={() => activeTab === 'server' ? fetchServerErrors() : fetchClientErrors()} 
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${(serverLoading || clientLoading) ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="server" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              Server Errors
              {serverStats?.unresolved_errors > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                  {serverStats.unresolved_errors}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="client" className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Client Errors
            </TabsTrigger>
          </TabsList>

          {/* Server Errors Tab */}
          <TabsContent value="server" className="space-y-6">
            {/* Server Stats Dashboard */}
            {serverStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total ({hoursFilter}h)</p>
                      <p className="text-2xl font-bold">{serverStats.total_errors}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Unresolved</p>
                      <p className="text-2xl font-bold">{serverStats.unresolved_errors}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Critical (1h)</p>
                      <p className="text-2xl font-bold">{serverStats.critical_last_hour}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Resolved</p>
                      <p className="text-2xl font-bold">{serverStats.resolved_errors}</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Top Endpoints Chart */}
            {serverStats?.by_endpoint?.length > 0 && (
              <Card className="p-4">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Most Affected Endpoints
                </h3>
                <div className="space-y-2">
                  {serverStats.by_endpoint.slice(0, 5).map((ep, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-mono truncate max-w-md">{ep.endpoint}</span>
                          <span className="text-muted-foreground">{ep.count} errors</span>
                        </div>
                        <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500/50 rounded-full"
                            style={{ width: `${(ep.count / serverStats.by_endpoint[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Server Filters */}
            <Card className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Filter by endpoint..."
                      value={serverFilter.endpoint}
                      onChange={(e) => setServerFilter({ ...serverFilter, endpoint: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Input
                  type="number"
                  placeholder="Status code"
                  value={serverFilter.status_code}
                  onChange={(e) => setServerFilter({ ...serverFilter, status_code: e.target.value })}
                  className="w-32"
                />
                <div className="flex gap-2">
                  <Button
                    variant={serverFilter.resolved === null ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setServerFilter({ ...serverFilter, resolved: null })}
                  >
                    All
                  </Button>
                  <Button
                    variant={serverFilter.resolved === false ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setServerFilter({ ...serverFilter, resolved: false })}
                  >
                    Unresolved
                  </Button>
                  <Button
                    variant={serverFilter.resolved === true ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setServerFilter({ ...serverFilter, resolved: true })}
                  >
                    Resolved
                  </Button>
                </div>
                {user?.role === 'owner' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkResolve}
                    className="text-orange-400 hover:text-orange-300"
                  >
                    <CheckCheck className="w-4 h-4 mr-1" />
                    Resolve All
                  </Button>
                )}
              </div>
            </Card>

            {/* Server Error List */}
            <div className="space-y-3">
              {serverLoading ? (
                <Card className="p-8 text-center text-muted-foreground">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading server errors...
                </Card>
              ) : serverErrors.length === 0 ? (
                <Card className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium">No server errors!</p>
                  <p className="text-muted-foreground">All systems running smoothly.</p>
                </Card>
              ) : (
                serverErrors.map((error) => (
                  <Card key={error.error_id} className={`overflow-hidden ${error.resolved ? 'opacity-60' : ''}`}>
                    <div 
                      className="p-4 cursor-pointer hover:bg-muted/30"
                      onClick={() => toggleExpand(error.error_id)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-2 h-2 mt-2 rounded-full ${error.resolved ? 'bg-green-500' : 'bg-red-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={getStatusColor(error.status_code)}>
                              {error.status_code}
                            </Badge>
                            <Badge className={getErrorTypeColor(error.type)}>
                              {error.type}
                            </Badge>
                            <span className="font-medium truncate">{error.message?.slice(0, 80)}</span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1 font-mono">
                              <Code className="w-3 h-3" />
                              {error.method} {error.endpoint}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(error.created_at)}
                            </span>
                            {error.user_email && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {error.user_email}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!error.resolved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResolveServer(error.error_id);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {expandedIds.has(error.error_id) ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedIds.has(error.error_id) && (
                      <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
                        {error.stack_trace && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                              <Code className="w-3 h-3" /> Stack Trace
                            </p>
                            <pre className="text-xs bg-black/30 p-3 rounded-lg overflow-auto max-h-60 font-mono whitespace-pre-wrap">
                              {error.stack_trace}
                            </pre>
                          </div>
                        )}
                        {error.request_body && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Request Body</p>
                            <pre className="text-xs bg-black/30 p-3 rounded-lg overflow-auto max-h-40 font-mono">
                              {error.request_body}
                            </pre>
                          </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Error ID</p>
                            <p className="font-mono text-xs">{error.error_id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">IP Address</p>
                            <p>{error.ip_address || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">User Agent</p>
                            <p className="truncate text-xs">{error.user_agent || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">User ID</p>
                            <p className="font-mono text-xs">{error.user_id || 'Anonymous'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>

            {/* Server Pagination */}
            {serverTotal > limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={serverPage === 0}
                  onClick={() => setServerPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {serverPage + 1} of {Math.ceil(serverTotal / limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={(serverPage + 1) * limit >= serverTotal}
                  onClick={() => setServerPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Client Errors Tab */}
          <TabsContent value="client" className="space-y-6">
            {/* Client Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Client Errors</p>
                    <p className="text-2xl font-bold">{clientTotal}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Unresolved</p>
                    <p className="text-2xl font-bold">{clientErrors.filter(e => !e.resolved).length}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                    <p className="text-2xl font-bold">{clientErrors.filter(e => e.resolved).length}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Client Filters */}
            <Card className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Filter by route..."
                      value={clientFilter.route}
                      onChange={(e) => setClientFilter({ ...clientFilter, route: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={clientFilter.resolved === null ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setClientFilter({ ...clientFilter, resolved: null })}
                  >
                    All
                  </Button>
                  <Button
                    variant={clientFilter.resolved === false ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setClientFilter({ ...clientFilter, resolved: false })}
                  >
                    Unresolved
                  </Button>
                  <Button
                    variant={clientFilter.resolved === true ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setClientFilter({ ...clientFilter, resolved: true })}
                  >
                    Resolved
                  </Button>
                </div>
              </div>
            </Card>

            {/* Client Error List */}
            <div className="space-y-3">
              {clientLoading ? (
                <Card className="p-8 text-center text-muted-foreground">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading client errors...
                </Card>
              ) : clientErrors.length === 0 ? (
                <Card className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium">No client errors found!</p>
                  <p className="text-muted-foreground">Everything is running smoothly.</p>
                </Card>
              ) : (
                clientErrors.map((error) => (
                  <Card key={error.error_id} className={`overflow-hidden ${error.resolved ? 'opacity-60' : ''}`}>
                    <div 
                      className="p-4 cursor-pointer hover:bg-muted/30"
                      onClick={() => toggleExpand(error.error_id)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-2 h-2 mt-2 rounded-full ${error.resolved ? 'bg-green-500' : 'bg-red-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate">{error.message}</p>
                            {error.errorInfo?.type && (
                              <Badge className={getErrorTypeColor(error.errorInfo.type)}>
                                {error.errorInfo.type}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {error.route || 'Unknown route'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(error.createdAt)}
                            </span>
                            {error.email && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {error.email}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!error.resolved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResolveClient(error.error_id);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {expandedIds.has(error.error_id) ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedIds.has(error.error_id) && (
                      <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
                        {error.stack && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                              <Code className="w-3 h-3" /> Stack Trace
                            </p>
                            <pre className="text-xs bg-black/30 p-3 rounded-lg overflow-auto max-h-60 font-mono">
                              {error.stack}
                            </pre>
                          </div>
                        )}
                        {error.componentStack && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Component Stack</p>
                            <pre className="text-xs bg-black/30 p-3 rounded-lg overflow-auto max-h-40 font-mono">
                              {error.componentStack}
                            </pre>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Error ID</p>
                            <p className="font-mono">{error.error_id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">User Agent</p>
                            <p className="truncate">{error.userAgent || 'Unknown'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>

            {/* Client Pagination */}
            {clientTotal > limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={clientPage === 0}
                  onClick={() => setClientPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {clientPage + 1} of {Math.ceil(clientTotal / limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={(clientPage + 1) * limit >= clientTotal}
                  onClick={() => setClientPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
